import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import {
  FaSearch,
  FaBoxOpen,
  FaExclamationCircle,
  FaWifi,
  FaRegPaperPlane,
} from 'react-icons/fa';
import Icon from '../../components/ui/Icon';
import Button from '../../components/ui/Button';
import { ScrollText, Reveal } from '../../components/motion';
import { trackShipment, TrackingError } from '../../api/tracking';
import type { JourneyResponse } from '../../types/tracking';
import JourneyMilestones from '../../components/tracking/JourneyMilestones';
import JourneyTimeline from '../../components/tracking/JourneyTimeline';
import ReportProblem from '../../components/tracking/ReportProblem';
import { useTrackingSocket } from '../../hooks/useTrackingSocket';
import usePageMeta from '../../hooks/usePageMeta';
import styles from './Tracking.module.css';

type Phase = 'idle' | 'loading' | 'error' | 'result';

type ErrorState = {
  kind: 'not_found' | 'network' | 'error';
  message: string;
};

const Tracking: React.FC = () => {
  usePageMeta({
    title: 'Track Your Cargo Shipment | Sahari Cargo',
    description:
      'Track your Sahari Cargo shipment from Saudi Arabia. Enter your AWB, box or MAWB number to see every stage from pickup to delivery.',
    url: 'https://www.saharicargo.com/tracking',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') ?? '';

  const [query, setQuery] = useState(urlQuery);
  const [phase, setPhase] = useState<Phase>('idle');
  const [journey, setJourney] = useState<JourneyResponse | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);

  // Tracks the latest request so a stale resolve never overwrites a newer one.
  const requestRef = useRef(0);
  // The identifier of the journey currently on screen — replayed by the live
  // socket refresh so it re-pulls the same shipment.
  const lastSearchedRef = useRef<string | null>(null);

  const runSearch = useCallback(async (raw: string) => {
    const id = raw.trim();
    if (!id) return;

    const token = ++requestRef.current;
    setPhase('loading');
    setError(null);
    setJourney(null);

    try {
      const result = await trackShipment(id);
      if (token !== requestRef.current) return;
      lastSearchedRef.current = id;
      setJourney(result);
      setPhase('result');
    } catch (err) {
      if (token !== requestRef.current) return;
      const te =
        err instanceof TrackingError
          ? { kind: err.kind, message: err.message }
          : {
              kind: 'error' as const,
              message: 'Something went wrong. Please try again.',
            };
      setError(te);
      setPhase('error');
    }
  }, []);

  // Live refresh: re-pull the current shipment in place, keeping the timeline
  // on screen (no loading flicker). Driven by the tracking socket — fires when
  // staff post a new scan OR flip an event between Public and Internal, so the
  // customer sees events appear/disappear without reloading. A transient
  // failure is swallowed so a hiccup never wipes the visible journey.
  const refetchSilently = useCallback(async () => {
    const id = lastSearchedRef.current;
    if (!id) return;
    const token = ++requestRef.current;
    try {
      const result = await trackShipment(id);
      if (token !== requestRef.current) return;
      setJourney(result);
      setPhase('result');
    } catch {
      /* keep the current timeline on a transient refresh failure */
    }
  }, []);

  const { connected: liveConnected } = useTrackingSocket(
    journey?.type ?? null,
    journey?.entityId ?? null,
    refetchSilently,
  );

  // Auto-search whatever is in `?q=` on mount and whenever it changes
  // (e.g. via a shared link or browser navigation).
  useEffect(() => {
    if (urlQuery.trim()) {
      setQuery(urlQuery);
      void runSearch(urlQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = query.trim();
    if (!id) return;
    // Push to the URL — the effect above picks it up and runs the search.
    // If the same query is re-submitted, kick the search directly.
    if (id === urlQuery.trim()) {
      void runSearch(id);
    } else {
      setSearchParams({ q: id });
    }
  };

  return (
    <section className={styles.page}>
      <Container fluid="xl" className={styles.inner}>
        <div className={styles.headerRow}>
          <ScrollText as="h1" className={styles.title} mode="enter">
            Track Your Shipment
          </ScrollText>

          <Reveal
            as="form"
            className={styles.searchForm}
            delay={0.12}
            onSubmit={handleSubmit}
          >
            <div className={styles.searchWrap}>
              <span className={styles.searchIcon} aria-hidden="true">
                <Icon icon={FaSearch} />
              </span>
              <input
                type="text"
                className={styles.input}
                placeholder="Enter your tracking number — AWB, box or MAWB number"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Tracking number"
                autoComplete="off"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              className={styles.trackBtn}
              disabled={phase === 'loading' || !query.trim()}
            >
              {phase === 'loading' ? 'Tracking…' : 'Track Now'}
            </Button>
          </Reveal>
        </div>

        <Reveal as="p" className={styles.subtitle} delay={0.05}>
          Check Your Shipment Status
        </Reveal>

        {/* ----- State machine ----- */}
        {phase === 'idle' && <IdleState />}
        {phase === 'loading' && <LoadingState />}
        {phase === 'error' && error && (
          <ErrorStateView error={error} onRetry={() => runSearch(query)} />
        )}
        {phase === 'result' && journey && (
          <div className={styles.result}>
            {journey && journey.pipeline && journey.pipeline.length > 0 ? (
              <>
                {/* The hero — where it is now, the route, the ETA. The ladder
                    beneath it is off: the real list follows, and it says the
                    same thing without having to guess which rung a status
                    belongs on. */}
                <JourneyMilestones
                  journey={journey}
                  live={liveConnected}
                  showStepper={false}
                />
                {/* The hero above already names the shipment and its status. */}
                <JourneyTimeline journey={journey} hideHeader />
              </>
            ) : (
              <EmptyJourneyState />
            )}
            {(journey.type === 'AWB' || journey.type === 'BOX') && (
              <ReportProblem awb={journey.type === 'AWB' ? journey.identifier : null} />
            )}
          </div>
        )}
      </Container>
    </section>
  );
};

export default Tracking;

/* ============================================================
   State views
   ============================================================ */

const IdleState: React.FC = () => (
  <Reveal className={styles.stateCard} delay={0.1}>
    <span className={`${styles.stateIcon} ${styles.stateIconNeutral}`}>
      <Icon icon={FaRegPaperPlane} />
    </span>
    <h2 className={styles.stateTitle}>Where is my shipment?</h2>
    <p className={styles.stateText}>
      Enter your tracking number above to see a live, step-by-step journey of
      your package — from pickup to delivery.
    </p>
    <div className={styles.formatHint}>
      <span className={styles.formatHintLabel}>Accepted formats</span>
      <div className={styles.formatChips}>
        <span className={styles.formatChip}>AWB number</span>
        <span className={styles.formatChip}>Box number</span>
        <span className={styles.formatChip}>MAWB number</span>
      </div>
    </div>
  </Reveal>
);

const LoadingState: React.FC = () => (
  <div className={styles.stateCard} aria-live="polite" aria-busy="true">
    <span className={styles.spinner} aria-hidden="true" />
    <h2 className={styles.stateTitle}>Tracking your shipment…</h2>
    <p className={styles.stateText}>
      Fetching the latest journey updates. This only takes a moment.
    </p>
    <div className={styles.skeletonList} aria-hidden="true">
      <span className={styles.skeletonRow} />
      <span className={styles.skeletonRow} />
      <span className={styles.skeletonRow} />
    </div>
  </div>
);

const ErrorStateView: React.FC<{
  error: ErrorState;
  onRetry: () => void;
}> = ({ error, onRetry }) => {
  const isNotFound = error.kind === 'not_found';
  const isNetwork = error.kind === 'network';
  return (
    <Reveal className={styles.stateCard} delay={0.05}>
      <span
        className={`${styles.stateIcon} ${
          isNotFound ? styles.stateIconWarn : styles.stateIconError
        }`}
      >
        <Icon icon={isNetwork ? FaWifi : FaExclamationCircle} />
      </span>
      <h2 className={styles.stateTitle}>
        {isNotFound
          ? 'No shipment found'
          : isNetwork
            ? 'Connection problem'
            : 'Something went wrong'}
      </h2>
      <p className={styles.stateText}>{error.message}</p>
      {isNotFound && (
        <p className={styles.stateHint}>
          Double-check the number and try again. AWB, box and MAWB numbers are
          all accepted.
        </p>
      )}
      <Button variant="primary" onClick={onRetry}>
        Try again
      </Button>
    </Reveal>
  );
};

const EmptyJourneyState: React.FC = () => (
  <div className={styles.stateCard}>
    <span className={`${styles.stateIcon} ${styles.stateIconNeutral}`}>
      <Icon icon={FaBoxOpen} />
    </span>
    <h2 className={styles.stateTitle}>We have your shipment</h2>
    <p className={styles.stateText}>
      No journey updates have been posted yet. Check back soon — this page
      updates as your package moves.
    </p>
  </div>
);
